/**
 * Ambient identity context used by the {@link Logger} to stamp
 * `sessionId`, `userId` and `correlationId` on every emitted event.
 *
 * A module-level singleton (not React Context) is used because non-React
 * callers — Redux middleware, RTK Query lifecycle hooks, plain `catch`
 * blocks — must be able to read the context synchronously without holding
 * a React render tree.
 *
 * All accessors are synchronous and never throw, so they are safe to call
 * from the {@link Logger} hot path.
 */

type LoggerContextState = {
    sessionId: string;
    userId: string;
    correlationId: string;
};

const EMPTY_CONTEXT: LoggerContextState = {
    sessionId: '',
    userId: '',
    correlationId: '',
};

let currentContext: LoggerContextState = EMPTY_CONTEXT;

/**
 * Generates a UUID v4 `sessionId` using the platform's cryptographically
 * secure random source (`crypto.randomUUID`). Available on all runtimes
 * this app supports:
 * - Web: Chrome 92+, Safari 15.4+, Firefox 95+ (2021+)
 * - Mobile (React Native ≥ 0.71 with Hermes) via `globalThis.crypto`
 *
 * If the API is somehow unavailable we throw rather than silently degrade
 * to `Math.random`, because CodeQL correctly flags that as insecure and
 * because the failure indicates a broken runtime the logger cannot recover
 * from.
 */
const generateSessionId = (): string => {
    const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (!cryptoRef?.randomUUID) {
        throw new Error('crypto.randomUUID is unavailable — logger cannot generate a sessionId');
    }
    return cryptoRef.randomUUID();
};

/**
 * Initialize the logger context for a fresh app launch. Generates a new
 * `sessionId` and resets `userId` and `correlationId` to empty strings.
 * Call once during application bootstrap, before the first `logger.*` call.
 */
export const initLoggerContext = (): void => {
    currentContext = {
        sessionId: generateSessionId(),
        userId: '',
        correlationId: '',
    };
};

/**
 * Read the current context. Returns a shallow copy so callers cannot
 * mutate the underlying singleton via the returned reference.
 */
export const getLoggerContext = (): Readonly<LoggerContextState> => ({ ...currentContext });

/** Set the logged-in user's pseudonymous identifier. MUST NOT be PII. */
export const setUser = (userId: string): void => {
    currentContext = { ...currentContext, userId };
};

/** Clear the user identifier (e.g. on logout). */
export const clearUser = (): void => {
    currentContext = { ...currentContext, userId: '' };
};

/**
 * Run `fn` with `correlationId` temporarily set. Restores the previous
 * correlation ID (typically empty) when `fn` completes or throws.
 *
 * Nesting is safe (save/restore). Two independent async flows sharing this
 * module are NOT isolated from each other — the last writer wins. Wrap
 * only one user flow at a time.
 *
 * Works for both sync and async `fn` — the returned promise resolves after
 * the restore step runs.
 */
export const withCorrelation = async <T>(correlationId: string, fn: () => T | Promise<T>): Promise<T> => {
    const previous = currentContext.correlationId;
    currentContext = { ...currentContext, correlationId };
    try {
        return await fn();
    } finally {
        currentContext = { ...currentContext, correlationId: previous };
    }
};

/**
 * Reset the context to its empty state. Primarily used by tests to
 * ensure isolation between suites; exported on the public barrel for
 * that reason.
 */
export const resetLoggerContext = (): void => {
    currentContext = EMPTY_CONTEXT;
};
